(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-peguu-help]";
  const MODAL_SELECTOR = "[data-peguu-help-modal]";
  const OPEN_SELECTOR = "[data-peguu-help-open]";
  const CLOSE_SELECTOR = "[data-peguu-help-close]";
  const TOGGLE_SELECTOR = "[data-peguu-help-toggle]";
  const STORAGE_KEY = "peguu-help-launcher-collapsed";

  class PeguuHelpModal {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector(MODAL_SELECTOR);
      this.dialog = root.querySelector("[data-peguu-help-dialog]");
      this.toggleButton = root.querySelector(TOGGLE_SELECTOR);
      this.closeButtons = root.querySelectorAll(CLOSE_SELECTOR);
      this.lastFocusedElement = null;
      this.isOpen = false;
      this.isCollapsed = false;

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.open = this.open.bind(this);
      this.close = this.close.bind(this);
      this.toggleCollapsed = this.toggleCollapsed.bind(this);

      this.init();
    }

    init() {
      this.isCollapsed = this.readCollapsed();
      this.syncCollapsed();

      document.addEventListener("click", this.handleDocumentClick);
      document.addEventListener("keydown", this.handleKeydown);

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", this.close);
      });
    }

    readCollapsed() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) === "true";
      } catch (error) {
        return false;
      }
    }

    writeCollapsed(collapsed) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, collapsed ? "true" : "false");
      } catch (error) {
        // El almacenamiento puede no estar disponible.
      }
    }

    syncCollapsed() {
      this.root.classList.toggle("is-collapsed", this.isCollapsed);

      if (!this.toggleButton) {
        return;
      }

      this.toggleButton.setAttribute("aria-expanded", this.isCollapsed ? "false" : "true");
      this.toggleButton.setAttribute(
        "aria-label",
        this.isCollapsed ? "Mostrar ayuda" : "Contraer ayuda"
      );
    }

    toggleCollapsed() {
      this.isCollapsed = !this.isCollapsed;
      this.writeCollapsed(this.isCollapsed);
      this.syncCollapsed();
    }

    handleDocumentClick(event) {
      const toggle = event.target.closest(TOGGLE_SELECTOR);

      if (toggle && this.root.contains(toggle)) {
        event.preventDefault();
        this.toggleCollapsed();
        return;
      }

      const opener = event.target.closest(OPEN_SELECTOR);

      if (!opener) {
        return;
      }

      event.preventDefault();
      this.open(opener);
    }

    handleKeydown(event) {
      if (!this.isOpen) {
        return;
      }

      if (event.key === "Escape") {
        this.close();
        return;
      }

      if (event.key === "Tab") {
        this.trapFocus(event);
      }
    }

    open(opener) {
      if (this.isOpen || !this.modal) {
        return;
      }

      this.isOpen = true;
      this.lastFocusedElement = opener || document.activeElement;

      this.root.classList.add("is-modal-open");
      this.modal.classList.add("is-open");
      this.modal.setAttribute("aria-hidden", "false");

      document.documentElement.classList.add("peguu-help-modal-open");
      document.body.classList.add("peguu-help-modal-open");

      window.requestAnimationFrame(() => {
        this.dialog?.focus();
      });
    }

    close() {
      if (!this.isOpen || !this.modal) {
        return;
      }

      this.isOpen = false;

      this.root.classList.remove("is-modal-open");
      this.modal.classList.remove("is-open");
      this.modal.setAttribute("aria-hidden", "true");

      document.documentElement.classList.remove("peguu-help-modal-open");
      document.body.classList.remove("peguu-help-modal-open");

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus === "function"
      ) {
        this.lastFocusedElement.focus();
      }
    }

    trapFocus(event) {
      const focusableElements = this.modal.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])'
        ].join(",")
      );

      const elements = Array.from(focusableElements).filter(
        (element) => element.offsetParent !== null
      );

      if (!elements.length) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  const initialize = () => {
    const root = document.querySelector(ROOT_SELECTOR);

    if (!root || root.dataset.peguuInitialized === "true") {
      return;
    }

    root.dataset.peguuInitialized = "true";
    window.PeguuHelpModal = new PeguuHelpModal(root);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
})();
